import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Socket, io } from 'socket.io-client';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class SocketService {
  private socket: Socket | null = null;
  private currentGroupId: string | null = null;
  // Registry of active listeners
  private listenerRegistry: Array<{
    eventName: string;
    sourceName: string;
    callback: (data: any) => void;
  }> = [];

  public reconnected$ = new BehaviorSubject<boolean>(false);

  constructor() {
    this.connect();
  }

  public reconnect(): void {
    if (!this.socket || !this.socket.connected) {
      this.connect();
    }
  }

  /**
   * Force the socket to re-handshake so the `auth` callback re-reads the current token.
   * The socket connects once at bootstrap (pre-login) with no token and then stays
   * connected, so a plain reconnect() — which only revives a *dead* socket — never
   * re-attaches the token after login. This tears the connection down and back up on the
   * same socket, which re-invokes auth(); the existing 'connect' handler re-emits
   * joinGroup and rebinds listeners.
   */
  public reauthenticate(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket.connect();
    } else {
      this.connect();
    }
  }

  public disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.currentGroupId = null;
    }
  }

  public joinGroup(groupId: string): void {
    if (this.currentGroupId && this.socket) {
      this.socket.emit('leaveGroup', { groupId: this.currentGroupId });
    }
    this.currentGroupId = groupId;
    if (this.socket) {
      this.socket.emit('joinGroup', { groupId });
    }
  }

  public emit(eventName: string, data: any): void {
    if (this.socket) {
      //console.log('socket emit: ', eventName, data)
      this.socket.emit(eventName, data);
    }
  }

  public listen(eventName: string, sourceName: string): Observable<any> {
    return new Observable((subscriber) => {
      const listener = (data: any) => {
        //console.log('socket listen: ', sourceName, eventName, data);
        subscriber.next(data);
      };

      //console.log('Adding listener:', eventName, sourceName);
      this.listenerRegistry.push({ eventName, sourceName, callback: listener });

      this.socket?.on(eventName, listener);

      return () => {
        this.socket?.off(eventName, listener);
        this.listenerRegistry = this.listenerRegistry.filter(
          (item) =>
            !(
              item.eventName === eventName &&
              item.sourceName === sourceName &&
              item.callback === listener
            )
        );
      };
    });
  }

  private connect(): void {
    /*   this.socket = io(environment.API_URL, {
        transports: ["polling"],
        //transports: ["websocket", "polling"],
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        autoConnect: false
      }); */
    this.socket = io(environment.API_URL, {
      //transports: ["polling"],
      transports: ["websocket", "polling"],
      withCredentials: true,
      // Attach the JWT to the socket handshake so the server can authenticate the
      // connection (server reads handshake.auth.token and expects a 'Bearer ' prefix).
      // A function so the current token is re-read on every (re)connect; send nothing
      // when there is no token yet (pre-login) so the server takes its no-token path.
      auth: (cb: (data: any) => void) => {
        const t = localStorage.getItem('token');
        cb(t ? { token: 'Bearer ' + t } : {});
      },
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      autoConnect: false
    });

    this.socket.connect();

    this.socket.on('connect', () => {
      console.log('Socket connected:', this.socket?.id);
      this.reconnected$.next(true);
      if (this.currentGroupId) {
        this.socket!.emit('joinGroup', { groupId: this.currentGroupId });
      }
      this.rebindListeners();
    });

    this.socket.on('reconnect', (attemptNumber: number) => {
      console.log(`Reconnected on attempt ${attemptNumber}`);
      this.reconnected$.next(true);
      if (this.currentGroupId) {
        this.socket!.emit('joinGroup', { groupId: this.currentGroupId });
      }
      this.rebindListeners();
    });

    this.socket.on('reconnect_attempt', (attemptNumber: number) => {
      console.log(`Reconnection attempt ${attemptNumber}`);
    });

    this.socket.on('reconnect_error', (error: Error) => {
      console.error(`Reconnection error: ${error.message}`);
    });

    this.socket.on('reconnect_failed', () => {
      console.error('Reconnection failed');
    });

    this.socket.on('disconnect', () => {
      console.log('Socket disconnected... Clearing listeners');
      this.listenerRegistry.forEach(({ eventName, sourceName, callback }) => {
        this.socket?.off(eventName, callback);
      });
      //this.currentGroupId = null;
    });
  }

  private rebindListeners(): void {
    if (!this.socket) return;
    this.listenerRegistry.forEach(({ eventName, sourceName, callback }) => {
      // Get the list of listeners for the event on the current socket instance.
      const existingListeners = this.socket?.listeners(eventName);
      // Only bind if the callback is not already attached.
      if (!existingListeners?.includes(callback)) {
        //console.log(`Rebinding listener: ${eventName} for ${sourceName}`);
        this.socket?.on(eventName, callback);
      } else {
        //console.log(`Listener already bound: ${eventName} for ${sourceName}`);
      }
    });
  }

  public simulateReconnect(): void {
    console.log('Simulating socket disconnect...');
    if (this.socket) {
      // Disconnect the socket manually.
      this.socket.disconnect();
    }
    // After a short delay, reconnect.
    setTimeout(() => {
      console.log('Simulating socket reconnect...');
      this.connect();
    }, 2000);
  }

}

